/**
 * Toast Component
 * Notification toast with auto-dismiss and queue management
 */

class ToastManager {
    constructor() {
        this.toasts = [];
        this.container = null;
        this.maxToasts = 5;
    }

    /**
     * Initialize toast container
     */
    init() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'toast-container';
            this.container.setAttribute('aria-live', 'polite');
            this.container.setAttribute('aria-atomic', 'true');
            document.body.appendChild(this.container);
        }
    }

    /**
     * Show toast notification
     */
    show(message, options = {}) {
        this.init();

        const toast = new Toast(message, {
            type: options.type || 'info', // success, error, warning, info
            duration: options.duration || 3000,
            dismissible: options.dismissible !== false,
            onClose: () => {
                this.remove(toast);
            }
        });

        // Remove oldest if at max
        if (this.toasts.length >= this.maxToasts) {
            this.toasts[0].close();
        }

        this.toasts.push(toast);
        toast.render(this.container);

        return toast;
    }

    /**
     * Show success toast
     */
    success(message, options = {}) {
        return this.show(message, { ...options, type: 'success' });
    }

    /**
     * Show error toast
     */
    error(message, options = {}) {
        return this.show(message, { ...options, type: 'error' });
    }

    /**
     * Show warning toast
     */
    warning(message, options = {}) {
        return this.show(message, { ...options, type: 'warning' });
    }

    /**
     * Show info toast
     */
    info(message, options = {}) {
        return this.show(message, { ...options, type: 'info' });
    }

    /**
     * Remove toast from queue
     */
    remove(toast) {
        const index = this.toasts.indexOf(toast);
        if (index > -1) {
            this.toasts.splice(index, 1);
        }
    }

    /**
     * Clear all toasts
     */
    clearAll() {
        this.toasts.forEach(toast => toast.close());
        this.toasts = [];
    }
}

class Toast {
    constructor(message, options = {}) {
        this.message = message;
        this.options = {
            type: options.type || 'info',
            duration: options.duration || 3000,
            dismissible: options.dismissible !== false,
            onClose: options.onClose || null
        };

        this.element = null;
        this.timer = null;
    }

    /**
     * Create toast DOM structure
     */
    create() {
        this.element = document.createElement('div');
        this.element.className = `toast toast-${this.options.type}`;
        this.element.setAttribute('role', 'alert');

        const icons = {
            success: '✓',
            error: '✕',
            warning: '⚠',
            info: 'ℹ'
        };

        this.element.innerHTML = `
            <div class="toast-icon">${icons[this.options.type]}</div>
            <div class="toast-message">${this.message}</div>
            ${this.options.dismissible ? `
                <button class="toast-close" aria-label="Close">×</button>
            ` : ''}
        `;

        if (this.options.dismissible) {
            const closeBtn = this.element.querySelector('.toast-close');
            closeBtn.addEventListener('click', () => this.close());
        }

        return this;
    }

    /**
     * Render toast
     */
    render(container) {
        if (!this.element) {
            this.create();
        }

        container.appendChild(this.element);

        // Trigger animation
        requestAnimationFrame(() => {
            this.element.classList.add('active');
        });

        // Auto-dismiss
        if (this.options.duration > 0) {
            this.timer = setTimeout(() => this.close(), this.options.duration);
        }

        return this;
    }

    /**
     * Close toast
     */
    close() {
        if (this.timer) {
            clearTimeout(this.timer);
        }

        this.element.classList.remove('active');

        setTimeout(() => {
            if (this.element && this.element.parentNode) {
                this.element.parentNode.removeChild(this.element);
            }

            if (this.options.onClose) {
                this.options.onClose();
            }
        }, 300);
    }
}

// Create singleton instance
export const toast = new ToastManager();

// Export for global access
window.toast = toast;
